/*
 * Infinite scroll for collection grids.
 *
 * Twelve products per portion, fetched as `?page=N&section_id=…` when the
 * sentinel under the grid comes within 600px of the viewport. There is no
 * "show more" button anywhere.
 *
 * An endless grid breaks three things, and this file exists mostly to unbreak
 * them:
 *
 *  1. The back button. Open a product from the fifth portion, press back, and
 *     a naive implementation drops you at the top of the catalog with twelve
 *     cards. The loaded pages and scroll offset are kept in sessionStorage and
 *     rebuilt on a back/forward navigation.
 *  2. The URL. `?page=` follows the portion actually on screen, so a shared or
 *     reloaded link lands near where the sender was.
 *  3. The footer. Handled in Liquid: the sticky bar carries the service links.
 *
 * All state is read from #cp-sentinel, which sits inside #AjaxGridWrapper. When
 * a filter swaps the wrapper's contents there is a new sentinel with new
 * numbers, and this module re-initialises from it.
 */
import { track } from '@theme/cp-ladder-ui';

const STATE_KEY = 'cp_catalog_state';
const ROOT_MARGIN = '600px 0px';

let observer = null;
let sentinel = null;
let grid = null;
let loading = false;
let ended = false;

/* The collection the page belongs to, and the stream currently being read —
 * which becomes the "next collection" once the first one runs out. */
let primary = null;
let stream = null;

function filterKey() {
  const params = new URLSearchParams(window.location.search);
  params.delete('page');
  params.sort();
  return params.toString();
}

function pageUrl(base, page, sectionId) {
  const url = new URL(base, window.location.origin);
  url.searchParams.set('page', String(page));
  url.searchParams.set('section_id', sectionId);
  return url.toString();
}

/*
 * A portion that never answers must not freeze the grid. Without a deadline a
 * hung request leaves `loading` true for good: no error, no retry, just a
 * catalog that silently stops at 24 cards. After 12s the request is dropped,
 * the flag clears, and the next scroll tries again.
 */
const FETCH_TIMEOUT = 12000;

function fetchPage(source, page) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT) : null;

  return fetch(pageUrl(source.base, page, source.sectionId), {
    headers: { Accept: 'text/html' },
    signal: controller ? controller.signal : undefined,
  })
    .then(function (response) {
      if (timer) clearTimeout(timer);
      if (!response.ok) throw new Error('page ' + page + ' → ' + response.status);
      return response.text();
    })
    .then(function (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const mark = doc.getElementById('cp-sentinel');

      return {
        items: Array.prototype.slice.call(doc.querySelectorAll('#product-grid > li')),
        pages: mark ? Number(mark.dataset.pages) : page,
        total: mark ? Number(mark.dataset.total) : 0,
      };
    });
}

/* Cards start at opacity 0 and wait for clean-reveal.js to mark them in view;
 * that observer only knows the cards that existed at load, so appended ones
 * are revealed here, a frame later so the transition still plays. */
function reveal(nodes) {
  requestAnimationFrame(function () {
    nodes.forEach(function (node) {
      node.querySelectorAll('.minimal-card').forEach(function (card) { card.classList.add('in-view'); });
    });
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    // Same stale-limit problem as in restore(): tell Lenis the page grew.
    if (window.lenis && typeof window.lenis.resize === 'function') window.lenis.resize();
  });
}

function adopt(items, source, page) {
  return items.map(function (item) {
    const node = document.importNode(item, true);
    node.dataset.page = String(page);
    node.dataset.cpSrc = source.isNext ? 'next' : 'primary';
    return node;
  });
}

function skeletons(show) {
  grid.querySelectorAll('.cp-skeleton').forEach(function (node) { node.remove(); });
  if (!show) return;

  for (let i = 0; i < 2; i += 1) {
    const node = document.createElement('li');
    node.className = 'cp-skeleton';
    node.setAttribute('aria-hidden', 'true');
    node.innerHTML =
      '<div class="cp-skeleton__media"></div><div class="cp-skeleton__line"></div><div class="cp-skeleton__line cp-skeleton__line--short"></div>';
    grid.appendChild(node);
  }
}

function paintBar(state) {
  const status = document.querySelector('[data-cp-scrollbar-status]');
  const fill = document.querySelector('[data-cp-scrollbar-fill]');
  if (!status || !primary) return;

  const loaded = grid.querySelectorAll('li.product-grid__item:not([data-cp-src="next"])').length;
  const total = primary.total || loaded;

  status.classList.toggle('is-done', state === 'done');

  if (state === 'loading') {
    status.innerHTML = '<span class="cp-scrollbar__spinner" aria-hidden="true"></span>Завантажуємо ще';
  } else if (state === 'done' || loaded >= total) {
    status.textContent = 'Усі ' + total + ' переглянуто';
    status.classList.add('is-done');
  } else {
    status.textContent = loaded + ' із ' + total;
  }

  if (fill) fill.style.width = Math.min(100, (loaded / Math.max(1, total)) * 100) + '%';
}

function endMarker(total) {
  const node = document.createElement('li');
  node.className = 'cp-grid-end';
  node.innerHTML =
    '<span class="cp-grid-end__chip">' +
    '<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
    'Усі ' + total + ' переглянуто</span>';
  grid.appendChild(node);
}

function loadNext() {
  if (loading || ended || !stream) return;

  if (stream.page >= stream.pages) {
    /* This stream is finished. Either hand over to the next collection, once,
     * or stop for good. */
    endMarker(stream.isNext ? stream.total : primary.total);

    if (!stream.isNext && primary.nextUrl) {
      const heading = document.createElement('li');
      heading.className = 'cp-grid-next';
      heading.textContent = 'Далі — ' + primary.nextTitle;
      grid.appendChild(heading);

      stream = { base: primary.nextUrl, sectionId: primary.sectionId, page: 0, pages: 1, total: 0, isNext: true };
      loadNext();
      return;
    }

    ended = true;
    if (observer) observer.disconnect();
    paintBar('done');
    return;
  }

  loading = true;
  const page = stream.page + 1;
  const source = stream;

  skeletons(true);
  if (!source.isNext) paintBar('loading');

  fetchPage(source, page)
    .then(function (result) {
      skeletons(false);

      const nodes = adopt(result.items, source, page);
      nodes.forEach(function (node) { grid.appendChild(node); });
      reveal(nodes);

      source.page = page;
      source.pages = result.pages;
      if (source.isNext) source.total = result.total;
      else primary.loaded.add(page);

      track('catalog_page_loaded', { page: page, source: source.isNext ? 'next_collection' : 'infinite' });

      loading = false;
      paintBar(source.isNext || page >= source.pages ? 'done' : 'idle');

      /* The observer reports crossings only: if the new cards did not push the
       * sentinel back out of range it never fires again, and a throttled or
       * backgrounded tab can skip the callback altogether. So the distance is
       * also checked by hand, here and on scroll. */
      if (nearEnd()) loadNext();
    })
    .catch(function (error) {
      skeletons(false);
      loading = false;
      paintBar('idle');
      console.error('[cp-infinite-scroll]', error);
    });
}

function nearEnd() {
  if (!sentinel || !sentinel.isConnected) return false;
  return sentinel.getBoundingClientRect().top < window.innerHeight + 600;
}

/* ---- `?page=` follows what is on screen ---- */

let urlFrame = null;

function syncUrl() {
  urlFrame = null;
  if (!grid) return;

  const items = grid.querySelectorAll('li.product-grid__item[data-page]:not([data-cp-src="next"])');
  let page = null;

  for (let i = 0; i < items.length; i += 1) {
    if (items[i].getBoundingClientRect().bottom > 120) {
      page = Number(items[i].dataset.page);
      break;
    }
  }
  /* Past the last card — in the footer's reveal zone — nothing is "on screen",
   * and the honest answer is the last portion, not the first. */
  if (!page && items.length && items[items.length - 1].getBoundingClientRect().bottom <= 120) {
    page = Number(items[items.length - 1].dataset.page);
  }
  if (!page) return;

  const url = new URL(window.location.href);
  const current = Number(url.searchParams.get('page') || 1);
  if (current === page) return;

  if (page <= 1) url.searchParams.delete('page');
  else url.searchParams.set('page', String(page));

  window.history.replaceState(window.history.state, '', url.toString());
}

window.addEventListener(
  'scroll',
  function () {
    /* A timer, not requestAnimationFrame: frames stop in a backgrounded tab,
     * and a throttle that can stall forever is worse than one that is 80ms
     * late. */
    if (urlFrame) return;
    urlFrame = setTimeout(function () {
      syncUrl();
      if (nearEnd()) loadNext();
    }, 80);
  },
  { passive: true }
);

/* ---- Position kept across product → back ---- */

function saveState() {
  if (!primary) return;

  try {
    sessionStorage.setItem(
      STATE_KEY,
      JSON.stringify({
        collection: primary.handle,
        key: filterKey(),
        pages: Array.from(primary.loaded).sort(function (a, b) { return a - b; }),
        scrollY: Math.round(window.scrollY),
      })
    );
  } catch (error) {}
}

window.addEventListener('pagehide', saveState);

function readState() {
  try {
    const entry = performance.getEntriesByType('navigation')[0];
    if (!entry || entry.type !== 'back_forward') return null;

    const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
    if (!saved || saved.collection !== primary.handle || saved.key !== filterKey()) return null;
    return saved;
  } catch (error) {
    return null;
  }
}

function restore(saved) {
  const missing = saved.pages.filter(function (page) { return !primary.loaded.has(page); });
  if (!missing.length && !saved.scrollY) return Promise.resolve();

  loading = true;
  if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  paintBar('loading');

  /* Sequential on purpose: the order of the grid is the order of the pages. */
  return missing
    .reduce(function (chain, page) {
      return chain.then(function () {
        return fetchPage(stream, page).then(function (result) {
          const nodes = adopt(result.items, stream, page);
          const after = Array.prototype.find.call(
            grid.querySelectorAll('li[data-page]'),
            function (item) { return Number(item.dataset.page) > page; }
          );

          nodes.forEach(function (node) {
            if (after) grid.insertBefore(node, after);
            else grid.appendChild(node);
          });
          reveal(nodes);

          primary.loaded.add(page);
          stream.page = Math.max(stream.page, page);
        });
      });
    }, Promise.resolve())
    .then(function () {
      loading = false;
      paintBar('idle');

      /*
       * Lenis caches the scroll limit from its last measure. The grid has just
       * grown by several portions, so without a resize() it clamps the jump to
       * the old, shorter document — a customer who was at 12 000px came back
       * to 10 000px, a different row of products. Native scroll goes first so
       * the position is right even if Lenis is absent or late.
       */
      function jump() {
        window.scrollTo(0, saved.scrollY);

        if (window.lenis && typeof window.lenis.scrollTo === 'function') {
          if (typeof window.lenis.resize === 'function') window.lenis.resize();
          window.lenis.scrollTo(saved.scrollY, { immediate: true, force: true });
        }
      }

      /*
       * The saved offset may not exist yet. The footer spacer gets its height
       * on window load, and on this store third-party widgets push `load` out
       * by many seconds — until then the document is one footer short and the
       * browser clamps the jump. So the position is re-applied as the page
       * grows, for up to eight seconds, and abandoned the instant the customer
       * touches the page: fighting a finger for the scroll is worse than being
       * a few hundred pixels off.
       */
      let attempts = 0;
      let cancelled = false;
      const cancel = function () { cancelled = true; };
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(function (type) {
        window.addEventListener(type, cancel, { once: true, passive: true });
      });

      requestAnimationFrame(jump);

      const timer = setInterval(function () {
        attempts += 1;
        const settled = Math.abs(window.scrollY - saved.scrollY) <= 40;

        if (cancelled || settled || attempts > 26) {
          clearInterval(timer);
          return;
        }
        jump();
      }, 300);
    })
    .catch(function (error) {
      loading = false;
      paintBar('idle');
      console.error('[cp-infinite-scroll] restore failed', error);
    });
}

/* ---- Boot, and re-boot when a filter swaps the grid ---- */

function init() {
  const mark = document.getElementById('cp-sentinel');
  grid = document.getElementById('product-grid');
  if (!mark || !grid || mark === sentinel) return;

  if (observer) observer.disconnect();
  sentinel = mark;
  loading = false;
  ended = false;

  const base = new URL(window.location.href);
  base.searchParams.delete('page');

  const page = Number(mark.dataset.page) || 1;

  primary = {
    handle: mark.dataset.collection,
    sectionId: mark.dataset.sectionId,
    total: Number(mark.dataset.total) || 0,
    nextUrl: mark.dataset.nextUrl || '',
    nextTitle: mark.dataset.nextTitle || '',
    loaded: new Set([page]),
  };

  stream = {
    base: base.pathname + base.search,
    sectionId: primary.sectionId,
    page: page,
    pages: Number(mark.dataset.pages) || 1,
    total: primary.total,
    isNext: false,
  };

  grid.querySelectorAll(':scope > li').forEach(function (item) { item.dataset.cpSrc = 'primary'; });
  paintBar('idle');

  observer = new IntersectionObserver(
    function (entries) {
      if (entries.some(function (entry) { return entry.isIntersecting; })) loadNext();
    },
    { rootMargin: ROOT_MARGIN }
  );

  const saved = readState();
  (saved ? restore(saved) : Promise.resolve()).then(function () {
    observer.observe(sentinel);
  });
}

init();

const wrapper = document.getElementById('AjaxGridWrapper');
if (wrapper) {
  new MutationObserver(function () { init(); }).observe(wrapper, { childList: true });
}
